import { merge, of, combineLatest } from 'rxjs';
import {
  tap,
  map,
  flatMap,
  switchMap,
  take,
  debounceTime,
  catchError,
  filter,
  distinctUntilChanged,
} from 'rxjs/operators';
import createRxStore, { actions, ofType, rxDispatch } from '..';
import {
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';
import {
  GlobalSearchState,
  GlobalSearchAction,
  GlobalSearchActionTypes,
} from '../../types/global-search';
import { SenseiApi } from '../../utils/api';

import SessionStore from '../session';
import { SessionStatus } from '../../types/session';

import { updateQueryStringKey } from '../../utils/rx-store-serialization';

export const initialState: GlobalSearchState = {
  searchText: '',
  results: RESOURCE_IDLE,
  selectedIndex: 0,
};

function reducer(
  state: GlobalSearchState,
  action: GlobalSearchAction,
): GlobalSearchState {
  switch (action.type) {
    case GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.text,
        results: action.text.length === 0 ? RESOURCE_IDLE : state.results,
      };
    case GlobalSearchActionTypes.GLOBAL_SEARCH_CLEAR_SEARCH_TEXT:
      return {
        ...state,
        searchText: '',
        selectedIndex: 0,
        results: RESOURCE_IDLE,
      };
    case GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SELECTED_INDEX:
      if (state.results.status === ResourceStatus.COMPLETE) {
        return { ...state, selectedIndex: action.index };
      } else {
        return state;
      }

    case GlobalSearchActionTypes.GLOBAL_SEARCH_RESOURCE_LOADING:
      return { ...state, results: RESOURCE_LOADING };
    case GlobalSearchActionTypes.GLOBAL_SEARCH_RESOURCE_COMPLETE:
      if (state.results.status !== ResourceStatus.LOADING) {
        return state;
      }
      return {
        ...state,
        selectedIndex: 0,
        results: { status: ResourceStatus.COMPLETE, data: action.data },
      };
    case GlobalSearchActionTypes.GLOBAL_SEARCH_RESOURCE_ERROR:
      return {
        ...state,
        results: { status: ResourceStatus.ERROR, error: action.error },
      };
    default:
      return state;
  }
}

const GlobalSearchStore = createRxStore(
  'GlobalSearchStote',
  initialState,
  reducer,
);

// SIDE EFFECT: make http request to server when the global search data changes
actions
  .pipe(
    ofType(GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SEARCH_TEXT),

    filter((action: any) => action.text.length > 0),
    tap(() => {
      rxDispatch({
        type: GlobalSearchActionTypes.GLOBAL_SEARCH_RESOURCE_LOADING,
      });
    }),

    debounceTime(500),

    switchMap(() =>
      combineLatest(
        GlobalSearchStore.pipe(take(1)),
        SessionStore.pipe(
          take(1),
          map((i) => (i.status === SessionStatus.LOGGED_IN ? i.token : null)),
        ),
      ),
    ),
    switchMap((obs) =>
      of(obs).pipe(
        flatMap(([state, accessToken]) =>
          SenseiApi.search(accessToken, state.searchText),
        ),
        catchError((error) => {
          rxDispatch({
            type: GlobalSearchActionTypes.GLOBAL_SEARCH_RESOURCE_ERROR,
            error,
          });
          return of();
        }),
      ),
    ),
  )
  .subscribe((response) => {
    rxDispatch({
      type: GlobalSearchActionTypes.GLOBAL_SEARCH_RESOURCE_COMPLETE,
      data: response.results,
    });
  });

export default GlobalSearchStore;
